/**
 * 标准配合兼容性规则。
 *
 * 文件职责：
 * 1. 按 SOLIDWORKS Standard Mates by Entity 建立实体组合规则；
 * 2. 输出所有标准配合类型的启用/禁用状态；
 * 3. 只依赖 MateEntityRef，不读取 UI 或运行期 Object3D。
 */

import {
  AssemblyMateType,
  MateEntityKind,
  type MateEntityRef,
} from '../definition';

/** 标准配合类型显示顺序。 */
export const STANDARD_MATE_TYPE_ORDER: readonly AssemblyMateType[] = [
  AssemblyMateType.Coincident,
  AssemblyMateType.Parallel,
  AssemblyMateType.Perpendicular,
  AssemblyMateType.Tangent,
  AssemblyMateType.Concentric,
  AssemblyMateType.Lock,
  AssemblyMateType.Distance,
  AssemblyMateType.Angle,
];

/**
 * 配合类型可用性。
 */
export interface AssemblyMateTypeAvailability {
  /** 配合类型。 */
  readonly mateType: AssemblyMateType;
  /** 是否可用。 */
  readonly enabled: boolean;
  /** 禁用原因。 */
  readonly disabledReason?: string;
}

/**
 * 配合兼容性结果。
 */
export interface AssemblyMateCompatibilityResult {
  /** 第一实体。 */
  readonly firstEntity: MateEntityRef;
  /** 第二实体。 */
  readonly secondEntity: MateEntityRef;
  /** 全部标准配合类型可用性。 */
  readonly mateTypes: readonly AssemblyMateTypeAvailability[];
}

/** 不支持原因。 */
const UNSUPPORTED_REASON = '当前实体组合不支持该标准配合。';

/** 同组件禁用原因。 */
const SAME_COMPONENT_REASON = '配合需要两个不同装配组件。';

/** 求解器暂未覆盖的泛化实体类型。*/
const SOLVER_UNSUPPORTED_ENTITY_KINDS = new Set<MateEntityKind>([
  MateEntityKind.Surface,
  MateEntityKind.Curve,
  MateEntityKind.Unsupported,
]);

/**
 * 将参考实体归一到 SOLIDWORKS 标准配合表使用的几何语义。
 *
 * @param kind 原始实体类型。
 * @returns 规则表实体类型。
 */
function normalizeRuleEntityKind(kind: MateEntityKind): MateEntityKind {
  if (kind === MateEntityKind.ReferencePlane) {
    return MateEntityKind.Plane;
  }
  if (kind === MateEntityKind.ReferenceAxis) {
    return MateEntityKind.Line;
  }
  if (kind === MateEntityKind.Origin || kind === MateEntityKind.CoordinateSystem) {
    return MateEntityKind.Point;
  }
  return kind;
}

/**
 * 判断实体组合是否已经超出当前标准配合求解器的可求解范围。
 *
 * 设计说明：
 * SOLIDWORKS 的规则表允许部分泛化曲面/曲线参与配合，但 PuppyCAD 当前求解器只支持已经能被
 * 解析为平面、轴、点、球等确定几何的实体。兼容性入口必须以“能否提交并求解”为准，避免 UI
 * 暴露一个看似可选、实际只能失败的 Mate 类型。
 *
 * @param first 第一个实体类型。
 * @param second 第二个实体类型。
 * @returns 是否包含当前求解器暂不支持的实体。
 */
function hasSolverUnsupportedEntityKind(first: MateEntityKind, second: MateEntityKind): boolean {
  return SOLVER_UNSUPPORTED_ENTITY_KINDS.has(normalizeRuleEntityKind(first))
    || SOLVER_UNSUPPORTED_ENTITY_KINDS.has(normalizeRuleEntityKind(second));
}

/**
 * 判断实体类型是否可以被当前求解器稳定读取为轴线。
 *
 * @param kind 实体类型。
 * @returns 是否具备轴线语义。
 */
function isAxisSolvableEntityKind(kind: MateEntityKind): boolean {
  const normalizedKind = normalizeRuleEntityKind(kind);
  return normalizedKind === MateEntityKind.Line
    || normalizedKind === MateEntityKind.ReferenceAxis
    || normalizedKind === MateEntityKind.CircularEdge
    || normalizedKind === MateEntityKind.Cylinder
    || normalizedKind === MateEntityKind.Cone;
}

/**
 * 判断圆锥描述符是否包含解析曲面重合所需的参数。
 *
 * @param entity 配合实体。
 * @returns 是否包含稳定圆锥顶点与半角。
 */
function hasSolvableConeDescriptor(entity: MateEntityRef): boolean {
  const geometry = entity.geometry as BRepGeometryDescriptor & Record<string, unknown>;
  return Array.isArray(geometry.apex)
    && typeof geometry.halfAngle === 'number'
    && Number.isFinite(geometry.halfAngle)
    && geometry.halfAngle > 0
    && geometry.halfAngle < Math.PI / 2;
}

/**
 * 读取圆锥半角。
 *
 * @param entity 配合实体。
 * @returns 有效半角；缺失时返回 null。
 */
function resolveConeHalfAngle(entity: MateEntityRef): number | null {
  if (!hasSolvableConeDescriptor(entity)) {
    return null;
  }
  const geometry = entity.geometry as BRepGeometryDescriptor & Record<string, unknown>;
  return geometry.halfAngle as number;
}

/**
 * 判断圆锥 Coincident 是否已具备当前求解器需要的解析输入。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @returns 是否可开放给 UI 创建。
 */
function canSolveConeSurfaceCoincident(first: MateEntityRef, second: MateEntityRef): boolean {
  const normalizedFirst = normalizeRuleEntityKind(first.entityKind);
  const normalizedSecond = normalizeRuleEntityKind(second.entityKind);
  const firstConeHalfAngle = normalizedFirst === MateEntityKind.Cone
    ? resolveConeHalfAngle(first)
    : null;
  const secondConeHalfAngle = normalizedSecond === MateEntityKind.Cone
    ? resolveConeHalfAngle(second)
    : null;

  if (normalizedFirst === MateEntityKind.Cone && firstConeHalfAngle === null) {
    return false;
  }
  if (normalizedSecond === MateEntityKind.Cone && secondConeHalfAngle === null) {
    return false;
  }
  if (firstConeHalfAngle !== null && secondConeHalfAngle !== null) {
    return Math.abs(firstConeHalfAngle - secondConeHalfAngle) <= 1e-9;
  }

  const otherKind = normalizedFirst === MateEntityKind.Cone ? normalizedSecond : normalizedFirst;
  return otherKind === MateEntityKind.Point || otherKind === MateEntityKind.CircularEdge;
}

/**
 * 判断当前求解器是否支持包含圆锥面的指定配合。
 *
 * 设计说明：
 * 1. SOLIDWORKS 支持更多圆锥面配合，但那些配合需要圆锥顶点、半角和母线求解；
 * 2. 当前求解器可在圆锥描述符包含 apex/halfAngle 时求解平面-圆锥、圆锥-球相切；
 * 3. Coincident 只在解析曲面重合求解器能验证圆锥参数时开放，避免 UI 创建看似成功但实际只按轴线求解的错误 Mate。
 *
 * @param first 第一实体类型。
 * @param second 第二实体类型。
 * @param mateType 配合类型。
 * @returns 当前求解器是否可靠支持。
 */
function isConeMateSupportedByCurrentSolver(
  first: MateEntityRef,
  second: MateEntityRef,
  mateType: AssemblyMateType,
): boolean {
  const normalizedFirst = normalizeRuleEntityKind(first.entityKind);
  const normalizedSecond = normalizeRuleEntityKind(second.entityKind);
  if (normalizedFirst !== MateEntityKind.Cone && normalizedSecond !== MateEntityKind.Cone) {
    return true;
  }
  if (mateType === AssemblyMateType.Concentric) {
    return isAxisSolvableEntityKind(normalizedFirst) && isAxisSolvableEntityKind(normalizedSecond);
  }
  if (mateType === AssemblyMateType.Tangent) {
    const cone = normalizedFirst === MateEntityKind.Cone ? first : second;
    const other = normalizedFirst === MateEntityKind.Cone ? normalizedSecond : normalizedFirst;
    const geometry = cone.geometry as BRepGeometryDescriptor & Record<string, unknown>;
    return (other === MateEntityKind.Plane || other === MateEntityKind.Sphere)
      && Array.isArray(geometry.apex)
      && typeof geometry.halfAngle === 'number'
      && Number.isFinite(geometry.halfAngle)
      && geometry.halfAngle > 0
      && geometry.halfAngle < Math.PI / 2;
  }
  if (mateType === AssemblyMateType.Coincident) {
    return canSolveConeSurfaceCoincident(first, second);
  }
  return mateType === AssemblyMateType.Parallel
    || mateType === AssemblyMateType.Perpendicular
    || mateType === AssemblyMateType.Angle;
}

/**
 * 创建无序实体键。
 *
 * @param left 左实体类型。
 * @param right 右实体类型。
 * @returns 规则键。
 */
function createEntityPairKey(left: MateEntityKind, right: MateEntityKind): string {
  return [normalizeRuleEntityKind(left), normalizeRuleEntityKind(right)].sort().join('|');
}

/**
 * 构建规则表项。
 *
 * @param left 左实体类型。
 * @param right 右实体类型。
 * @param mateTypes 可用配合类型。
 * @returns 规则键和值。
 */
function createRuleEntry(
  left: MateEntityKind,
  right: MateEntityKind,
  mateTypes: readonly AssemblyMateType[],
): readonly [string, readonly AssemblyMateType[]] {
  return [createEntityPairKey(left, right), mateTypes] as const;
}

/**
 * 解析兼容性结果中的配合类型展示顺序。
 *
 * 设计说明：
 * 1. SOLIDWORKS 会按当前实体组合给出默认配合类型，圆柱/圆柱这类轴向实体应优先同轴心；
 * 2. `STANDARD_MATE_TYPE_ORDER` 只适合作为完整按钮列表的兜底顺序，不能覆盖实体规则自身的默认优先级。
 *
 * @param availableTypes 当前实体组合可用的配合类型，顺序即默认优先级。
 * @returns 含可用项和禁用项的完整展示顺序。
 */
function resolveCompatibilityMateTypeOrder(
  availableTypes: readonly AssemblyMateType[],
): readonly AssemblyMateType[] {
  const availableTypeSet = new Set(availableTypes);
  return [
    ...availableTypes,
    ...STANDARD_MATE_TYPE_ORDER.filter((mateType) => !availableTypeSet.has(mateType)),
  ];
}

/** SOLIDWORKS 标准配合实体组合规则表。 */
const STANDARD_MATE_RULES = new Map<string, readonly AssemblyMateType[]>([
  createRuleEntry(MateEntityKind.Plane, MateEntityKind.Plane, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Parallel,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Distance,
    AssemblyMateType.Angle,
  ]),
  createRuleEntry(MateEntityKind.Line, MateEntityKind.Line, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Parallel,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Distance,
    AssemblyMateType.Angle,
  ]),
  createRuleEntry(MateEntityKind.ReferenceAxis, MateEntityKind.ReferenceAxis, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Parallel,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Distance,
    AssemblyMateType.Angle,
  ]),
  createRuleEntry(MateEntityKind.Line, MateEntityKind.ReferenceAxis, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Parallel,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Distance,
    AssemblyMateType.Angle,
  ]),
  createRuleEntry(MateEntityKind.Plane, MateEntityKind.Line, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Parallel,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Distance,
  ]),
  createRuleEntry(MateEntityKind.Plane, MateEntityKind.ReferenceAxis, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Parallel,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Distance,
  ]),
  createRuleEntry(MateEntityKind.Point, MateEntityKind.Point, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Distance,
  ]),
  createRuleEntry(MateEntityKind.Point, MateEntityKind.Line, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Distance,
  ]),
  createRuleEntry(MateEntityKind.Point, MateEntityKind.ReferenceAxis, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Distance,
  ]),
  createRuleEntry(MateEntityKind.Point, MateEntityKind.Plane, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Distance,
  ]),
  createRuleEntry(MateEntityKind.CircularEdge, MateEntityKind.CircularEdge, [
    AssemblyMateType.Concentric,
  ]),
  createRuleEntry(MateEntityKind.CircularEdge, MateEntityKind.Cylinder, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Concentric,
  ]),
  createRuleEntry(MateEntityKind.CircularEdge, MateEntityKind.Cone, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Concentric,
  ]),
  createRuleEntry(MateEntityKind.CircularEdge, MateEntityKind.Line, [
    AssemblyMateType.Concentric,
  ]),
  createRuleEntry(MateEntityKind.CircularEdge, MateEntityKind.Plane, [
    AssemblyMateType.Coincident,
  ]),
  createRuleEntry(MateEntityKind.CircularEdge, MateEntityKind.ReferenceAxis, [
    AssemblyMateType.Concentric,
  ]),
  createRuleEntry(MateEntityKind.Cylinder, MateEntityKind.Cylinder, [
    AssemblyMateType.Concentric,
    AssemblyMateType.Parallel,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Distance,
    AssemblyMateType.Angle,
    AssemblyMateType.Tangent,
  ]),
  createRuleEntry(MateEntityKind.Cylinder, MateEntityKind.Cone, [
    AssemblyMateType.Concentric,
    AssemblyMateType.Parallel,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Angle,
  ]),
  createRuleEntry(MateEntityKind.Cone, MateEntityKind.Cone, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Concentric,
    AssemblyMateType.Parallel,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Distance,
    AssemblyMateType.Angle,
  ]),
  createRuleEntry(MateEntityKind.Cylinder, MateEntityKind.Line, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Concentric,
    AssemblyMateType.Parallel,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Distance,
    AssemblyMateType.Angle,
    AssemblyMateType.Tangent,
  ]),
  createRuleEntry(MateEntityKind.Cylinder, MateEntityKind.ReferenceAxis, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Concentric,
    AssemblyMateType.Parallel,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Distance,
    AssemblyMateType.Angle,
    AssemblyMateType.Tangent,
  ]),
  createRuleEntry(MateEntityKind.Cone, MateEntityKind.Line, [
    AssemblyMateType.Concentric,
    AssemblyMateType.Parallel,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Angle,
  ]),
  createRuleEntry(MateEntityKind.Cone, MateEntityKind.ReferenceAxis, [
    AssemblyMateType.Concentric,
    AssemblyMateType.Parallel,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Angle,
  ]),
  createRuleEntry(MateEntityKind.Plane, MateEntityKind.Cylinder, [
    AssemblyMateType.Distance,
    AssemblyMateType.Tangent,
  ]),
  createRuleEntry(MateEntityKind.Plane, MateEntityKind.Cone, [
    AssemblyMateType.Tangent,
  ]),
  createRuleEntry(MateEntityKind.Plane, MateEntityKind.Sphere, [
    AssemblyMateType.Distance,
    AssemblyMateType.Tangent,
  ]),
  createRuleEntry(MateEntityKind.Line, MateEntityKind.Sphere, [
    AssemblyMateType.Concentric,
    AssemblyMateType.Distance,
    AssemblyMateType.Tangent,
  ]),
  createRuleEntry(MateEntityKind.ReferenceAxis, MateEntityKind.Sphere, [
    AssemblyMateType.Concentric,
    AssemblyMateType.Distance,
    AssemblyMateType.Tangent,
  ]),
  createRuleEntry(MateEntityKind.Cylinder, MateEntityKind.Sphere, [
    AssemblyMateType.Concentric,
    AssemblyMateType.Tangent,
  ]),
  createRuleEntry(MateEntityKind.Cone, MateEntityKind.Sphere, [
    AssemblyMateType.Tangent,
  ]),
  createRuleEntry(MateEntityKind.Point, MateEntityKind.Cylinder, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Concentric,
    AssemblyMateType.Distance,
  ]),
  createRuleEntry(MateEntityKind.Point, MateEntityKind.Cone, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Concentric,
  ]),
  createRuleEntry(MateEntityKind.Point, MateEntityKind.Sphere, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Concentric,
    AssemblyMateType.Distance,
  ]),
  createRuleEntry(MateEntityKind.Point, MateEntityKind.Curve, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Distance,
  ]),
  createRuleEntry(MateEntityKind.Sphere, MateEntityKind.Sphere, [
    AssemblyMateType.Concentric,
    AssemblyMateType.Distance,
    AssemblyMateType.Tangent,
  ]),
  createRuleEntry(MateEntityKind.Surface, MateEntityKind.Point, [
    AssemblyMateType.Coincident,
  ]),
  createRuleEntry(MateEntityKind.Surface, MateEntityKind.Line, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Tangent,
  ]),
  createRuleEntry(MateEntityKind.Surface, MateEntityKind.Plane, [
    AssemblyMateType.Tangent,
  ]),
  createRuleEntry(MateEntityKind.Surface, MateEntityKind.Cylinder, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Tangent,
  ]),
  createRuleEntry(MateEntityKind.Surface, MateEntityKind.Cone, [
    AssemblyMateType.Coincident,
    AssemblyMateType.Perpendicular,
    AssemblyMateType.Tangent,
  ]),
]);

/**
 * 解析当前实体组合可由几何求解器稳定处理的标准配合类型。
 *
 * 设计说明：
 * 1. Surface/Curve/Unsupported 目前只能保守禁用几何配合，避免创建无法验证的 Mate；
 * 2. Lock 是组件级相对位姿约束，不属于几何配合，因此不在这里返回；
 * 3. 通过拆分职责，后续补齐非解析曲面求解时只需要扩展几何类型能力，不会影响 Lock。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @returns 可由当前几何求解器处理的标准配合类型。
 */
function resolveSolverSupportedGeometryMateTypes(
  first: MateEntityRef,
  second: MateEntityRef,
): readonly AssemblyMateType[] {
  if (hasSolverUnsupportedEntityKind(first.entityKind, second.entityKind)) {
    return [];
  }

  const baseTypes = STANDARD_MATE_RULES.get(createEntityPairKey(first.entityKind, second.entityKind)) ?? [];
  return baseTypes.filter((mateType) => isConeMateSupportedByCurrentSolver(
    first,
    second,
    mateType,
  ));
}

/**
 * 解析当前实体组合的标准配合类型。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @returns 可用配合类型集合。
 */
export function resolveAvailableStandardMateTypes(
  first: MateEntityRef,
  second: MateEntityRef,
): readonly AssemblyMateType[] {
  if (first.componentInstanceId === second.componentInstanceId) {
    return [];
  }

  const solverSupportedTypes = resolveSolverSupportedGeometryMateTypes(first, second);

  // Lock 是组件间位置和方向约束，SOLIDWORKS 中作为标准配合独立存在。
  return Array.from(new Set([...solverSupportedTypes, AssemblyMateType.Lock]));
}

/**
 * 计算标准配合类型可用性。
 *
 * @param first 第一实体。
 * @param second 第二实体。
 * @returns 兼容性结果。
 */
export function resolveAssemblyMateCompatibility(
  first: MateEntityRef,
  second: MateEntityRef,
): AssemblyMateCompatibilityResult {
  const availableTypes = resolveAvailableStandardMateTypes(first, second);
  const availableTypeSet = new Set(availableTypes);
  const sameComponent = first.componentInstanceId === second.componentInstanceId;

  return {
    firstEntity: first,
    secondEntity: second,
    mateTypes: resolveCompatibilityMateTypeOrder(availableTypes).map((mateType) => {
      const enabled = availableTypeSet.has(mateType);
      return enabled
        ? { mateType, enabled: true }
        : {
            mateType,
            enabled: false,
            disabledReason: sameComponent
              ? SAME_COMPONENT_REASON
              : UNSUPPORTED_REASON,
          };
    }),
  };
}

/**
 * 判断指定配合类型是否可用。
 *
 * @param result 兼容性结果。
 * @param mateType 配合类型。
 * @returns 是否可用。
 */
export function isAssemblyMateTypeEnabled(
  result: AssemblyMateCompatibilityResult,
  mateType: AssemblyMateType,
): boolean {
  return result.mateTypes.some((item) => item.mateType === mateType && item.enabled);
}
